import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  Handshake, Building2, Globe, CheckCircle2, ArrowRight,
  Mail, Shield, Star, Users, Code2, BarChart3, ChevronDown,
  Megaphone, Package, Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';

// ─── Data ─────────────────────────────────────────────────────────────────────

const PARTNER_TYPES = [
  {
    icon: Building2,
    title: 'Agency Partner',
    tagline: 'For digital agencies & studios',
    color: 'blue',
    perks: [
      'White-label the platform under your brand',
      'Client management dashboard',
      'Bulk generation credits at agency rates',
      'Co-branded sales collateral',
      'Dedicated partner success manager',
      'Priority support queue',
    ],
  },
  {
    icon: Package,
    title: 'Reseller Partner',
    tagline: 'For SaaS vendors & hosting providers',
    color: 'purple',
    perks: [
      'Resell BuildersLab under your brand',
      'Revenue share on all converted customers',
      'API access for deep integration',
      'Joint go-to-market strategy',
      'Co-marketing budget allocation',
      'Quarterly business reviews',
    ],
  },
  {
    icon: Layers,
    title: 'Integration Partner',
    tagline: 'For platforms & tools',
    color: 'orange',
    perks: [
      'Native plugin / embed access',
      'Sandbox environment for development',
      'Listed in our official integrations directory',
      'Technical integration support',
      'Early access to new APIs',
      'Joint press releases on launch',
    ],
  },
  {
    icon: Megaphone,
    title: 'Strategic Partner',
    tagline: 'For enterprises & media companies',
    color: 'emerald',
    perks: [
      'Custom commercial terms',
      'Executive relationship & roadmap input',
      'Co-developed product features',
      'Shared audience activations',
      'Exclusive pricing agreements',
      'Joint case study development',
    ],
  },
];

const BENEFITS = [
  { icon: BarChart3, title: 'Shared Revenue', body: 'Earn recurring income on every customer you bring in — with no cap on your potential.' },
  { icon: Globe, title: 'Global Co-Marketing', body: 'Reach our growing user base of developers, agencies, and founders through joint campaigns.' },
  { icon: Shield, title: 'Priority Access', body: 'Be the first to test new features, beta products, and AI model upgrades before public release.' },
  { icon: Users, title: 'Partner Community', body: 'Join an exclusive network of builders, founders, and growth experts shaping the future of the web.' },
  { icon: Code2, title: 'Full API Access', body: 'Deep, documented API integration so BuildersLab fits seamlessly into any workflow or product.' },
  { icon: Star, title: 'Listed Publicly', body: "Get featured in BuildersLab's official partner directory — driving inbound leads directly to you." },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Apply Online', body: 'Fill out the partner application below. Our team reviews every submission within 5 business days.' },
  { step: '02', title: 'Discovery Call', body: 'We hop on a 30-minute call to understand your business, goals, and which partnership tier fits best.' },
  { step: '03', title: 'Agreement & Onboarding', body: 'Sign the partner agreement and get full access to your dashboard, resources, and support channel.' },
  { step: '04', title: 'Grow Together', body: 'Launch joint initiatives, track performance in your partner portal, and scale your revenue together.' },
];

const PARTNER_TYPES_SELECT = [
  'Please select…',
  'Digital Agency',
  'SaaS / Software Company',
  'Hosting / Infrastructure Provider',
  'Marketing Platform',
  'Enterprise / Corporate',
  'Media / Content Company',
  'Freelancer / Consultant',
  'Other',
];

const GOALS_SELECT = [
  'Please select…',
  'White-label & resell the platform',
  'Deep API integration',
  'Co-marketing & audience growth',
  'Revenue sharing partnership',
  'Strategic / enterprise collaboration',
  'Not sure yet — want to explore',
];

const EMPTY_FORM = {
  name: '', email: '', company: '', website: '',
  partnerType: '', goal: '', audience: '', message: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const colorMap = {
  blue:    { bg: 'bg-blue-500/10',   border: 'border-blue-500/20',   icon: 'text-blue-400',   badge: 'bg-blue-500/15 text-blue-400 border-blue-500/25' },
  purple:  { bg: 'bg-purple-500/10', border: 'border-purple-500/20', icon: 'text-purple-400', badge: 'bg-purple-500/15 text-purple-400 border-purple-500/25' },
  orange:  { bg: 'bg-orange-500/10', border: 'border-orange-500/20', icon: 'text-orange-400', badge: 'bg-orange-500/15 text-orange-400 border-orange-500/25' },
  emerald: { bg: 'bg-emerald-500/10',border: 'border-emerald-500/20',icon: 'text-emerald-400',badge: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' },
};

function InputField({ label, id, type = 'text', value, onChange, placeholder, required }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-zinc-300">
        {label}{required && <span className="text-orange-500 ml-1">*</span>}
      </label>
      <input
        id={id} type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder} required={required}
        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all"
      />
    </div>
  );
}

function SelectField({ label, id, value, onChange, options, required }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-zinc-300">
        {label}{required && <span className="text-orange-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <select
          id={id} value={value}
          onChange={e => onChange(e.target.value)}
          required={required}
          style={{ colorScheme: 'dark' }}
          className="w-full appearance-none bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all pr-10"
        >
          {options.map(opt => (
            <option key={opt} value={opt.startsWith('Please') ? '' : opt} className="bg-zinc-900">{opt}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Partnership() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: '00ed92cb-5032-4578-b9be-306700b2271e',
          botcheck: false,
          subject: `Partnership Application — ${form.name} · ${form.company || 'No company'} · ${form.partnerType || 'TBD'}`,
          from_name: form.name,
          email: form.email,
          company: form.company || 'Not provided',
          website: form.website || 'Not provided',
          partner_type: form.partnerType || 'Not specified',
          partnership_goal: form.goal || 'Not specified',
          audience_size: form.audience || 'Not provided',
          message: form.message || 'No message',
        }),
      });
      const data = await res.json();
      if (data.success) { setStatus('success'); setForm(EMPTY_FORM); }
      else throw new Error(data.message || 'Submission failed');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
      setTimeout(() => setStatus('idle'), 6000);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <SEOHead
        title="Partnership Opportunities"
        description="Partner with BuildersLab to deliver AI-powered web solutions. Explore agency, reseller, and technology integration partnerships."
        ogUrl="https://builderslab.net/Partnership"
      />
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative pt-36 pb-28 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-blue-500/6 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-orange-500/6 blur-[120px] rounded-full pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 text-sm text-blue-400 font-medium mb-6">
              <Handshake className="w-4 h-4" />
              Partner Programme
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6">
              Build More,{' '}
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">
                Together
              </span>
            </h1>

            <p className="text-xl text-zinc-400 leading-relaxed max-w-2xl mx-auto mb-10">
              Join the BuildersLab partner ecosystem. Whether you're an agency, a SaaS platform,
              or an enterprise — we have a structure built to make both our businesses grow faster.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="#apply"
                className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-semibold shadow-[0_0_30px_rgba(59,130,246,0.3)] hover:shadow-[0_0_40px_rgba(59,130,246,0.45)] transition-all group"
              >
                Apply to Partner
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="mailto:partnerships@builderslab.net"
                className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-white font-medium transition-all"
              >
                <Mail className="w-4 h-4" />
                partnerships@builderslab.net
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────────────────────────────── */}
      <section className="py-12 border-y border-white/5 bg-white/[0.01]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '200+', label: 'Active Partners' },
              { value: '40+', label: 'Countries Represented' },
              { value: '$1.2M+', label: 'Partner Revenue Generated' },
              { value: '48h', label: 'Application Response Time' },
            ].map((s, i) => (
              <div
                key={s.label}
               
               
               
               
              >
                <div className="text-3xl font-extrabold text-white mb-1">{s.value}</div>
                <div className="text-sm text-zinc-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Partner Types ────────────────────────────────────────────────── */}
      <section className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div
           
           
           
           
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
              Choose Your Partnership Track
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              We don't do one-size-fits-all. Every partnership is structured
              around what creates the most value for both sides.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {PARTNER_TYPES.map((pt, i) => {
              const c = colorMap[pt.color];
              return (
                <div
                  key={pt.title}
                 
                 
                 
                 
                  className={`group flex flex-col bg-white/[0.03] border ${c.border} rounded-2xl p-6 hover:bg-white/[0.05] transition-all`}
                >
                  <div className={`w-11 h-11 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center mb-4 shrink-0`}>
                    <pt.icon className={`w-5 h-5 ${c.icon}`} />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-0.5">{pt.title}</h3>
                  <p className={`text-xs font-medium mb-4 ${c.icon}`}>{pt.tagline}</p>
                  <ul className="space-y-2.5 flex-1">
                    {pt.perks.map(perk => (
                      <li key={perk} className="flex items-start gap-2.5 text-sm text-zinc-400">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${c.icon}`} />
                        {perk}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#apply"
                    className={`mt-6 w-full h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 border ${c.border} ${c.bg} ${c.icon} hover:opacity-80 transition-opacity`}
                  >
                    Apply for This Track
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Benefits ─────────────────────────────────────────────────────── */}
      <section className="py-24 border-y border-white/5 bg-white/[0.015]">
        <div className="max-w-5xl mx-auto px-6">
          <div
           
           
           
            className="text-center mb-14"
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
              What You Get as a Partner
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Real, tangible benefits — not just a logo on a webpage.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <div
                key={b.title}
               
               
               
               
                className="group bg-white/[0.03] border border-white/8 rounded-2xl p-5 hover:border-orange-500/25 hover:bg-white/[0.05] transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-4 group-hover:bg-orange-500/15 transition-colors">
                  <b.icon className="w-5 h-5 text-orange-400" />
                </div>
                <h3 className="text-white font-semibold mb-1.5">{b.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────────── */}
      <section className="py-28">
        <div className="max-w-4xl mx-auto px-6">
          <div
           
           
           
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
              How Partnering Works
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              From application to active partnership in under two weeks.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {HOW_IT_WORKS.map((step, i) => (
              <div
                key={step.step}
               
               
               
               
                className="flex gap-5 bg-white/[0.03] border border-white/8 rounded-2xl p-6 hover:border-blue-500/25 transition-colors"
              >
                <div className="shrink-0 w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <span className="text-blue-400 font-extrabold text-sm">{step.step}</span>
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1.5">{step.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Application Form ─────────────────────────────────────────────── */}
      <section id="apply" className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.06)_0%,transparent_65%)] pointer-events-none" />
        <div className="relative max-w-2xl mx-auto px-6">
          <div
           
           
           
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 text-sm text-blue-400 font-medium mb-5">
              <Handshake className="w-3.5 h-3.5" />
              Partner Application
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 tracking-tight">
              Ready to Build Together?
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Tell us a bit about your business. Our partnership team will respond within 48 hours.
            </p>
          </div>

          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <div
                key="success"
               
               
               
                className="bg-emerald-500/10 border border-emerald-500/25 rounded-3xl p-12 text-center"
              >
                <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Application Received!</h3>
                <p className="text-zinc-400 leading-relaxed max-w-sm mx-auto">
                  Thank you for applying. Our partnership team will review your application
                  and reach out within 48 hours with next steps.
                </p>
                <div className="mt-8 flex items-center justify-center gap-2 text-sm text-zinc-500">
                  <Mail className="w-4 h-4" />
                  <span>Check your inbox for a confirmation</span>
                </div>
              </div>
            ) : (
              <div key="form">
                <div className="relative">
                  <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-br from-blue-500/20 via-transparent to-purple-500/15 blur-sm" />
                  <div className="relative bg-zinc-950 border border-white/10 rounded-3xl p-8 sm:p-10 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <InputField label="Full Name" id="name" value={form.name} onChange={set('name')} placeholder="Jane Smith" required />
                        <InputField label="Work Email" id="email" type="email" value={form.email} onChange={set('email')} placeholder="jane@company.com" required />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <InputField label="Company Name" id="company" value={form.company} onChange={set('company')} placeholder="Acme Agency" required />
                        <InputField label="Company Website" id="website" type="url" value={form.website} onChange={set('website')} placeholder="https://acme.agency" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <SelectField label="Organisation Type" id="partnerType" value={form.partnerType} onChange={set('partnerType')} options={PARTNER_TYPES_SELECT} required />
                        <SelectField label="Partnership Goal" id="goal" value={form.goal} onChange={set('goal')} options={GOALS_SELECT} required />
                      </div>
                      <InputField
                        label="Audience / Client Base Size"
                        id="audience"
                        value={form.audience}
                        onChange={set('audience')}
                        placeholder="e.g. 5,000 monthly users / 120 agency clients"
                      />
                      <div className="space-y-1.5">
                        <label htmlFor="message" className="block text-sm font-medium text-zinc-300">
                          Tell us about your vision for this partnership
                        </label>
                        <textarea
                          id="message" value={form.message}
                          onChange={e => set('message')(e.target.value)}
                          rows={4}
                          placeholder="How do you see BuildersLab fitting into your product or service? What outcomes are you aiming for?"
                          className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all resize-none"
                        />
                      </div>

                      <AnimatePresence>
                        {status === 'error' && (
                          <p
                           
                           
                           
                            className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3"
                          >
                            {errorMsg}
                          </p>
                        )}
                      </AnimatePresence>

                      <Button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full h-13 py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-semibold rounded-xl shadow-[0_0_20px_rgba(59,130,246,0.25)] hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] transition-all disabled:opacity-60 disabled:cursor-not-allowed group text-base"
                      >
                        {status === 'loading' ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Submitting Application…
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            Submit Partnership Application
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        )}
                      </Button>

                      <p className="text-center text-xs text-zinc-600">
                        Your information is kept strictly confidential.
                        We respond to every application within 48 business hours.
                      </p>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ── Bottom CTA ───────────────────────────────────────────────────── */}
      <section className="border-t border-white/5 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-zinc-500 text-sm mb-2">Prefer to reach the team directly?</p>
          <a
            href="mailto:partnerships@builderslab.net"
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-semibold transition-colors text-lg"
          >
            <Mail className="w-5 h-5" />
            partnerships@builderslab.net
          </a>
        </div>
      </section>

    </div>
  );
}
